import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { useAuth } from '@context/auth/base';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, UserSoundCollectionFragmentDoc } from '@graphql/reactQuery';
import { ArtistAppearsOnCarousel } from '../Carousels/ArtistAppearsOn';
import { CollectionCarousel } from '../Carousels/Collection';
import { CollectorAppearsOnCarousel } from '../Carousels/CollectorAppearsOn';
import { PlaylistsCarousel } from '../Carousels/Playlists';
import { ReleasesCarousel } from '../Carousels/Releases';
import { ArtistRecentActivity } from './ArtistRecentActivity';
import { CollectorRecentActivity } from './CollectorRecentActivity';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment UserSoundCollection on User {
    id
    ...UserInfoSection
    artist {
      id
      ...ArtistAppearsOn
      ...ReleasesCarouselInfo
    }
    ...CollectionCarousel
    ...CollectorAppearsOn
    ...PlaylistsCarouselUserInfo
    ...SeeAllUserInfo
    shelvesCount(filter: { type: ALL })
    likedSounds {
      id
      releaseCount
    }
  }
`);

export const SoundCollections = ({
  user,
}: {
  user: FragmentType<UserSoundCollectionFragmentDoc>;
}) => {
  const userData = getFragment(UserSoundCollectionFragmentDoc, user);
  const { authUser, isConnectingAPIUser } = useAuth();
  const isOwner = authUser?.id === userData.id;
  const isArtist = !!userData.artist;

  // Playlist section should be shown if user has playlists or is the owner
  // If a user has no playlists, but has liked sounds, we still want to show the liked sounds section
  const hasPlaylists = !!userData.shelvesCount || !!userData.likedSounds?.releaseCount;

  return (
    <Container>
      {isArtist && userData.artist && (
        <>
          <ReleasesCarousel artistFrag={userData.artist} />
          <ArtistAppearsOnCarousel artistFrag={userData.artist} />
        </>
      )}
      {!userData.artist && <CollectorAppearsOnCarousel userFrag={userData} />}
      <CollectionCarousel userFrag={userData} />
      {(hasPlaylists || isOwner) && (
        <PlaylistsCarousel
          userId={userData.id}
          user={userData}
          isOwner={isConnectingAPIUser ? undefined : isOwner}
        />
      )}
      {userData.artist ? (
        <ArtistRecentActivity user={userData} artistId={userData.artist.id} />
      ) : (
        <CollectorRecentActivity user={userData} userId={userData.id} />
      )}
    </Container>
  );
};

const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$6',
  paddingTop: '$6',
  paddingX: '$5',

  '@laptop': {
    gap: '$12',
    paddingTop: '$12',
    paddingX: 0,
  },
});
